import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Wallet as WalletIcon, Plus, ArrowUpRight, ArrowDownLeft, Loader2, Globe } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PKG_META = {
  starter: { amount: 5, tag: "" },
  standard: { amount: 10, tag: "Popular" },
  plus: { amount: 25, tag: "" },
  pro: { amount: 50, tag: "Best value" },
};

const REGION_CURRENCY = {
  AE: "aed", GB: "gbp", CA: "cad", AU: "aud", IN: "inr", KE: "kes", NG: "ngn",
  GH: "ghs", ZA: "zar", SA: "sar", QA: "qar", CH: "chf", SE: "sek", NO: "nok",
  DK: "dkk", JP: "jpy", SG: "sgd", HK: "hkd", NZ: "nzd", MX: "mxn",
  DE: "eur", FR: "eur", ES: "eur", IT: "eur", NL: "eur", IE: "eur", PT: "eur",
  FI: "eur", AT: "eur", BE: "eur", GR: "eur",
};

function guessCurrency() {
  try {
    const loc = Intl.DateTimeFormat().resolvedOptions().locale || navigator.language || "";
    const region = loc.split("-").pop().toUpperCase();
    return REGION_CURRENCY[region] || "usd";
  } catch {
    return "usd";
  }
}

function fmtLocal(usd, cur) {
  if (!cur || cur.code === "usd") return null;
  const v = usd * cur.rate;
  const amount = cur.code === "jpy" ? Math.round(v).toLocaleString() : v.toFixed(2);
  return `${cur.symbol}${amount}`;
}

export default function Wallet() {
  const { user, setBalance, refreshUser } = useAuth();
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(null);
  const [params, setParams] = useSearchParams();
  const [verifying, setVerifying] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [currency, setCurrency] = useState("usd");

  useEffect(() => {
    api.get("/currencies").then(({ data }) => {
      setCurrencies(data.currencies);
      const preferred = user?.preferred_currency || guessCurrency();
      if (data.currencies.some((c) => c.code === preferred)) setCurrency(preferred);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const selectedCurrency = currencies.find((c) => c.code === currency);

  const changeCurrency = (code) => {
    setCurrency(code);
    api.put("/profile", {
      phone: user?.phone || "",
      callback_number: user?.callback_number || "",
      preferred_currency: code,
    }).then(({ data }) => refreshUser && refreshUser()).catch(() => {});
  };

  const loadWallet = useCallback(async () => {
    const { data } = await api.get("/wallet");
    setLedger(data.ledger);
    setBalance(data.balance);
  }, [setBalance]);

  const pollStatus = useCallback(
    async (sessionId, attempts = 0) => {
      if (attempts >= 6) {
        setVerifying(false);
        toast.error("Payment status check timed out. Refresh to see your balance.");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setBalance(data.balance);
          setVerifying(false);
          try { const a = new Audio("/sounds/05_zeeptel_success.wav"); a.volume = 0.5; a.play().catch(() => {}); } catch { /* no audio */ }
          toast.success("Top-up successful! Credit added to your wallet.");
          loadWallet();
          refreshUser();
          return;
        }
        if (data.status === "expired") {
          setVerifying(false);
          toast.error("Payment session expired. Please try again.");
          return;
        }
        setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
      } catch (e) {
        setVerifying(false);
        toast.error("Could not verify payment.");
      }
    },
    [setBalance, loadWallet, refreshUser]
  );

  useEffect(() => {
    loadWallet();
    const sid = params.get("session_id");
    if (sid) {
      setVerifying(true);
      pollStatus(sid);
      params.delete("session_id");
      setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line

  const topUp = async (packageId) => {
    setLoading(packageId);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setLoading(null);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Balance card */}
      <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-blue-800 text-white p-8 shadow-xl relative overflow-hidden mb-8" data-testid="wallet-balance-card">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10" />
        <div className="absolute -right-2 top-16 h-24 w-24 rounded-full bg-white/5" />
        <div className="flex items-center gap-2 text-blue-200 text-sm font-semibold uppercase tracking-wide mb-2">
          <WalletIcon className="h-4 w-4" /> Wallet balance
        </div>
        <div className="font-mono-dial text-5xl font-bold">${(user?.balance ?? 0).toFixed(2)}</div>
        {verifying && (
          <div className="mt-3 flex items-center gap-2 text-sm text-primary" data-testid="payment-verifying">
            <Loader2 className="h-4 w-4 animate-spin" /> Verifying your payment…
          </div>
        )}
      </div>

      {/* Top-up packages */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-heading text-xl font-bold tracking-tight">Add credit</h2>
        {currencies.length > 0 && (
          <Select value={currency} onValueChange={changeCurrency}>
            <SelectTrigger className="w-auto min-w-[150px] h-9 rounded-xl border-black/10 bg-white text-sm" data-testid="currency-select-trigger">
              <Globe className="h-3.5 w-3.5 text-zinc-400 mr-1.5" />
              <SelectValue placeholder="Currency" />
            </SelectTrigger>
            <SelectContent>
              {currencies.map((c) => (
                <SelectItem key={c.code} value={c.code} data-testid={`currency-option-${c.code}`}>
                  <span className="font-medium">{c.code.toUpperCase()}</span>
                  <span className="text-zinc-400 ml-2 text-xs">{c.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {currency !== "usd" && selectedCurrency && (
        <p className="text-xs text-zinc-500 -mt-2 mb-4" data-testid="currency-note">
          You'll pay in {selectedCurrency.name} ({currency.toUpperCase()}) — your wallet is always credited in US dollars.
        </p>
      )}
      <div className="grid grid-cols-2 gap-4 mb-10">
        {Object.entries(PKG_META).map(([id, m]) => {
          const local = fmtLocal(m.amount, selectedCurrency);
          return (
            <button
              key={id}
              onClick={() => topUp(id)}
              disabled={loading !== null}
              data-testid={`topup-${id}`}
              className="relative bg-white border border-black/10 rounded-2xl p-6 text-left hover:border-primary hover:shadow-md transition-all active:scale-[0.98] disabled:opacity-60"
            >
              {m.tag && (
                <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-accent text-accent-foreground text-[10px] font-bold uppercase">
                  {m.tag}
                </span>
              )}
              <div className="text-xs uppercase tracking-wide text-zinc-400 font-bold capitalize">{id}</div>
              <div className="font-mono-dial text-3xl font-bold mt-1">{local || `$${m.amount}`}</div>
              {local && (
                <div className="text-xs text-zinc-400 font-mono mt-0.5">= ${m.amount} credit</div>
              )}
              <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                {loading === id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Top up
              </div>
            </button>
          );
        })}
      </div>

      {/* Ledger */}
      <h2 className="font-heading text-xl font-bold tracking-tight mb-4">Transactions</h2>
      <div className="bg-white border border-black/10 rounded-2xl shadow-sm overflow-hidden" data-testid="wallet-ledger">
        {ledger.length === 0 ? (
          <div className="p-8 text-center text-zinc-400">No transactions yet.</div>
        ) : (
          ledger.map((t) => (
            <div key={t.id} className="flex items-center gap-3 px-5 py-3.5 border-b border-black/5 last:border-0">
              <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${t.amount >= 0 ? "bg-accent" : "bg-zinc-100"}`}>
                {t.amount >= 0 ? (
                  <ArrowDownLeft className="h-4 w-4 text-primary" />
                ) : (
                  <ArrowUpRight className="h-4 w-4 text-zinc-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{t.description}</div>
                <div className="text-xs text-zinc-400">{new Date(t.created_at).toLocaleString()}</div>
              </div>
              <div className={`font-mono font-bold ${t.amount >= 0 ? "text-primary" : "text-zinc-700"}`}>
                {t.amount >= 0 ? "+" : ""}${Math.abs(t.amount).toFixed(2)}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
